// Built-ins
import React from "react";

// Icons / Images
import { BsInfoSquare } from "react-icons/bs";
import { FaHashtag, FaEnvelope, FaPhone, FaMobile, FaSort } from "react-icons/fa";
import { MdSecurity, MdCheckCircle, MdCancel, MdPerson, MdLock, MdTask, MdCheckCircleOutline } from "react-icons/md";
import { GiMoneyStack } from "react-icons/gi";
import { FaUserTag } from "react-icons/fa";

// Modules / Functions
import { Card } from "react-bootstrap";
import { Grid } from "semantic-ui-react";
 
import Swal from "sweetalert2";

// Custom Made Components
import NavigationBar from "../../../core/navigation_bar/navigation_bar";
import Footer from "../../../core/footer/footer";
import DeleteObjectModal from "../../../modals/delete_object";
import AddProjectModal from "../../../modals/create/add_project";
import AddCashModal from "../../../modals/create/add_cash";
import {
  EditConsultantIdModal,
  EditConsultantFullnameModal,
  EditConsultantEmailModal,
  EditConsultantPhoneModal,
  EditConsultantMobileModal,
  EditConsultantRoleModal,
  EditConsultantUsernameModal,
  EditConsultantPasswordModal,
  EditConsultantCanAssignTaskModal,
  EditConsultantCashPassportModal,
  EditConsultantActiveModal,
  EditConsultantOrderIndexModal,
  EditConsultantPhotoModal,
} from "../../../modals/consultant_edit_modals";
import axios from "axios";

// Global Variables
import { headers,
  pageHeader,
  loader, formatDisplayDate, formatDisplayDateTime } from "../../../global_vars";
import RecordAuditBlock from "../../../core/record_audit_block";
import AccountingTotalSummary from "../../../common/AccountingTotalSummary";
import { AccountingCurrencyRateBar } from "../../../common/AccountingCurrencyRatePanel";
import CashTransactionActions from "../../../common/CashTransactionActions";
import RichTextDisplay from "../../../common/RichTextDisplay";

// API endpoint for consultant
const VIEW_CONSULTANT = "https://ultima.icsgr.com/api/administration/consultant/";

// Helpers to read URL like: /administration/consultant/<consultant_id>
function getConsultantIdFromPath() {
  const parts = window.location.pathname.split("/").filter(Boolean); // removes '' from // or trailing /
  const idx = parts.indexOf("consultant");
  if (idx === -1) return null;
  const id = parts[idx + 1] || null;
  return id ? decodeURIComponent(id) : null;
}

let overviewIconStyle = { color: "#93ab3c", marginRight: "0.5em" };
let labelPillStyle = { background: "#eef5ff", color: "#2c3e50", padding: "2px 10px", borderRadius: "12px", fontSize: "0.85em", marginRight: "8px", border: "1px solid #d6e4ff" };
let valueTextStyle = { fontWeight: 600, color: "#212529" };

class ConsultantOverview extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      consultant: {},
      is_loaded: false,
      projects: [],
      cashItems: [],
      tasksByProject: {}, // { [project_id]: { assignedToMe: [], assignedByMe: [] } }
      taskCommentsByProject: {}, // { [project_id]: { [task_id]: comments[] } }
      countryTitleById: {},
    };
  }

  formatCashPassportDisplay = (cashpassport) => {
    if (!cashpassport) return "None";
    const map = this.state.countryTitleById || {};
    const names = String(cashpassport)
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .map((id) => map[id] || id);
    return names.length ? names.join(", ") : "None";
  };

  componentDidMount() {
    // Update headers with current token
    const currentHeaders = {
      ...headers,
      "Authorization": "Token " + localStorage.getItem("userToken")
    };

    const consultantId = getConsultantIdFromPath();

    const countriesPromise = axios
      .get("https://ultima.icsgr.com/api/regions/all_countries/", { headers: currentHeaders })
      .then((r) => r?.data?.all_countries || [])
      .catch(() => []);

    axios
      .get(`${VIEW_CONSULTANT}${consultantId}/`, { headers: currentHeaders })
      .then(async (res) => {
        const consultant = res?.data || {};

        // Projects
        let projects = consultant.projects || [];
        if (!Array.isArray(projects) || projects.length === 0) {
          try {
            const projRes = await axios.get("https://ultima.icsgr.com/api/data_management/projects/", { headers: currentHeaders });
            const allProjects = projRes?.data?.all_projects || projRes?.data?.results || projRes?.data?.data || projRes?.data || [];
            const cidStr = String(consultant.consultant_id || "");
            projects = (Array.isArray(allProjects) ? allProjects : []).filter((p) => {
              const c = p?.consultant;
              if (!c) return false;
              const id = c?.consultant_id ?? c?.id ?? c;
              return String(id) === cidStr;
            });
          } catch (_e) { projects = []; }
        }

        // Cash
        let cashItems = consultant.cash || [];
        if (!Array.isArray(cashItems) || cashItems.length === 0) {
          try {
            const cashRes = await axios.get("https://ultima.icsgr.com/api/data_management/cash/", { headers: currentHeaders });
            const allCash = cashRes?.data?.all_cash || cashRes?.data?.results || cashRes?.data?.data || cashRes?.data || [];
            const cidStr = String(consultant.consultant_id ?? "");
            cashItems = (Array.isArray(allCash) ? allCash : []).filter((c) => {
              const cons = c?.consultant;
              const consId = cons && (cons.consultant_id ?? cons.id ?? cons);
              return String(consId ?? "") === cidStr;
            });
          } catch (_e) { cashItems = []; }
        }


        // Build tasks per project (assigned to me / assigned by me)
        let tasksByProject = {};
        let taskCommentsByProject = {};
        let aggregatedCash = [];
        try {
          const detailPromises = (projects || []).map((p) =>
            axios.get(`https://ultima.icsgr.com/api/data_management/project/${p.project_id}/`, { headers: currentHeaders })
          );
          const details = await Promise.all(detailPromises.map((pr) => pr.catch((e) => ({ error: e }))));
          details.forEach((dr, idx) => {
            const proj = projects[idx];
            if (!proj) return;
            const tasks = Array.isArray(dr?.data?.tasks) ? dr.data.tasks : [];
            // Show all tasks under each project
            tasksByProject[proj.project_id] = { all: tasks };

            // Build comments map per task for this project
            const projComments = Array.isArray(dr?.data?.task_comments) ? dr.data.task_comments : [];
            const mapByTask = {};
            projComments.forEach((cm) => {
              const tid = cm?.projtask?.projtask_id || cm?.projtask_id;
              if (!tid) return;
              if (!mapByTask[tid]) mapByTask[tid] = [];
              mapByTask[tid].push(cm);
            });
            taskCommentsByProject[proj.project_id] = mapByTask;

            // Aggregate cash transactions from project details
            const cashList = Array.isArray(dr?.data?.cash_transactions) ? dr.data.cash_transactions : [];
            cashList.forEach((c) => aggregatedCash.push(c));
          });
        } catch (_e) {
          tasksByProject = {};
          taskCommentsByProject = {};
        }

        // Prefer project-aggregated cash over consultant-level fallback
        if (aggregatedCash.length) {
          cashItems = aggregatedCash;
        }

        const countries = await countriesPromise;
        const countryTitleById = {};
        (Array.isArray(countries) ? countries : []).forEach((c) => {
          if (c?.country_id) countryTitleById[c.country_id] = c.title || c.country_id;
        });

        this.setState({ consultant, projects, cashItems, tasksByProject, taskCommentsByProject, countryTitleById, is_loaded: true });
      })
      .catch((e) => {
        if (e?.response?.status === 401) {
          this.setState({ forbidden: true });
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "An unknown error has occurred.",
          });
        }
      });
  }

  // When modals return a fresh object, replace state.consultant
  update_state = (updated) => {
    this.setState({ consultant: updated });
  };

  render() {
    const { consultant } = this.state;
    
    return (
      <>
        <NavigationBar />
        <style>{`
          .pillLink { color: inherit; text-decoration: none; }
          .pillLink:hover { color: #93ab3c !important; text-decoration: none; }
        `}</style>
        <div className="mainContainer">
          {pageHeader("consultant_overview", `Consultant: ${consultant.fullname || 'Loading...'}`)}
          {this.state.is_loaded ? (
            <>
              <RecordAuditBlock creator={consultant.record_creator} createdAt={consultant.record_created_at} />
              {/* New Consultant Photo and Name Section */}
              <Card style={{ marginBottom: "20px", backgroundColor: "#f8f9fa" }}>
                <Card.Body style={{ padding: "30px" }}>
                  <div style={{ 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: "flex-start",
                    gap: "40px"
                  }}>
                    {/* Photo Section */}
                    <div style={{ position: "relative" }}>
                      {consultant.photo_url ? (
                        <img
                          src={consultant.photo_url}
                          alt="Consultant"
                          style={{
                            width: "150px",
                            height: "150px",
                            borderRadius: "50%",
                            objectFit: "cover",
                            border: "4px solid #93ab3c",
                            boxShadow: "0 4px 8px rgba(0,0,0,0.1)"
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: "150px",
                            height: "150px",
                            borderRadius: "50%",
                            backgroundColor: "#e9ecef",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            border: "4px solid #93ab3c",
                            boxShadow: "0 4px 8px rgba(0,0,0,0.1)"
                          }}
                        >
                          <MdPerson style={{ fontSize: "75px", color: "#6c757d" }} />
                        </div>
                      )}
                      {/* Photo Edit Button */}
                      <div style={{ 
                        marginTop: "10px",
                        textAlign: "center"
                      }}>
                        <EditConsultantPhotoModal
                          consultant={consultant}
                          update_state={this.update_state}
                        />
                      </div>
                    </div>

                    {/* Name Section */}
                    <div style={{ textAlign: "left" }}>
                      <h1 style={{ 
                        margin: "0", 
                        fontSize: "2.5rem", 
                        fontWeight: "bold",
                        color: "#93ab3c"
                      }}>
                        {consultant.fullname || "N/A"}
                      </h1>
                      <p style={{ 
                        margin: "10px 0 0 0", 
                        fontSize: "1.2rem", 
                        color: "#6c757d",
                        fontWeight: "500"
                      }}>
                        Consultant ID: {consultant.consultant_id || "N/A"}
                      </p>
                      <div style={{ marginTop: "15px" }}>
                        <EditConsultantFullnameModal
                          consultant={consultant}
                          update_state={this.update_state}
                        />
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>

              <Grid stackable columns={2} divided>
                <Grid.Column>
                  <Card>
                    <Card.Header>
                      <BsInfoSquare
                        style={{
                          color: "#93ab3c",
                          fontSize: "1.5em",
                          marginRight: "0.5em",
                        }}
                      />
                      Basic Information
                    </Card.Header>
                    <Card.Body>
                      {/* Consultant ID */}
                      <div className={"info_descr"}>
                        <FaHashtag style={overviewIconStyle} /> Consultant ID
                      </div>
                      <div className={"info_span"} style={{ position: "relative" }}>
                        {consultant.consultant_id ? consultant.consultant_id : "N/A"}
                        <span style={{ position: "absolute", right: "0px", top: "50%", transform: "translateY(-50%)" }}>
                          <EditConsultantIdModal
                            consultant={consultant}
                            update_state={this.update_state}
                          />
                        </span>
                      </div>

                      {/* E-mail */}
                      <div className={"info_descr"} style={{ marginTop: 16 }}>
                        <FaEnvelope style={overviewIconStyle} /> E-mail
                      </div>
                      <div className={"info_span"} style={{ position: "relative" }}>
                        {consultant.email ? consultant.email : "N/A"}
                        <span style={{ position: "absolute", right: "0px", top: "50%", transform: "translateY(-50%)" }}>
                          <EditConsultantEmailModal
                            consultant={consultant}
                            update_state={this.update_state}
                          />
                        </span>
                      </div>

                      {/* Telephone */}
                      <div className={"info_descr"} style={{ marginTop: 16 }}>
                        <FaPhone style={overviewIconStyle} /> Telephone
                      </div>
                      <div className={"info_span"} style={{ position: "relative" }}>
                        {consultant.phone ? consultant.phone : "N/A"}
                        <span style={{ position: "absolute", right: "0px", top: "50%", transform: "translateY(-50%)" }}>
                          <EditConsultantPhoneModal
                            consultant={consultant}
                            update_state={this.update_state}
                          />
                        </span>
                      </div>

                      {/* Cell phone */}
                      <div className={"info_descr"} style={{ marginTop: 16 }}>
                        <FaMobile style={overviewIconStyle} /> Cell phone
                      </div>
                      <div className={"info_span"} style={{ position: "relative" }}>
                        {consultant.mobile ? consultant.mobile : "N/A"}
                        <span style={{ position: "absolute", right: "0px", top: "50%", transform: "translateY(-50%)" }}>
                          <EditConsultantMobileModal
                            consultant={consultant}
                            update_state={this.update_state}
                          />
                        </span>
                      </div>

                      {/* Order by */}
                      <div className={"info_descr"} style={{ marginTop: 16 }}>
                        <FaSort style={overviewIconStyle} /> Order by
                      </div>
                      <div className={"info_span"} style={{ position: "relative" }}>
                        {(typeof consultant.orderindex === "number" ||
                          typeof consultant.orderindex === "string")
                          ? consultant.orderindex
                          : "N/A"}
                        <span style={{ position: "absolute", right: "0px", top: "50%", transform: "translateY(-50%)" }}>
                          <EditConsultantOrderIndexModal
                            consultant={consultant}
                            update_state={this.update_state}
                          />
                        </span>
                      </div>
                    </Card.Body>
                    <Card.Footer>
                      <DeleteObjectModal
                        objectId={consultant.consultant_id}
                        objectName={consultant.fullname}
                        objectType="Consultant"
                        onObjectDeleted={() => {
                          window.location.href = "/administration/all_consultants";
                        }}
                      />
                    </Card.Footer>
                  </Card>
                </Grid.Column>
                <Grid.Column>
                  <Card>
                    <Card.Header>
                      <MdSecurity
                        style={{
                          color: "#93ab3c",
                          fontSize: "1.5em",
                          marginRight: "0.5em",
                        }}
                      />
                      Account Information
                    </Card.Header>
                    <Card.Body>
                      {/* Username */}
                      <div className={"info_descr"}>
                        <MdPerson style={overviewIconStyle} /> Username
                      </div>
                      <div className={"info_span"} style={{ position: "relative" }}>
                        {consultant.username ? consultant.username : "N/A"}
                        <span style={{ position: "absolute", right: "0px", top: "50%", transform: "translateY(-50%)" }}>
                          <EditConsultantUsernameModal
                            consultant={consultant}
                            update_state={this.update_state}
                          />
                        </span>
                      </div>

                      {/* Password */}
                      <div className={"info_descr"} style={{ marginTop: 16 }}>
                        <MdLock style={overviewIconStyle} /> Password
                      </div>
                      <div className={"info_span"} style={{ position: "relative" }}>
                        ********
                        <span style={{ position: "absolute", right: "0px", top: "50%", transform: "translateY(-50%)" }}>
                          <EditConsultantPasswordModal
                            consultant={consultant}
                            update_state={this.update_state}
                          />
                        </span>
                      </div>

                      {/* Role */}
                      <div className={"info_descr"} style={{ marginTop: 16 }}>
                        <FaUserTag style={overviewIconStyle} /> Role
                      </div>
                      <div className={"info_span"} style={{ position: "relative" }}>
                        {consultant.role === 'A' ? 'Admin' :
                         consultant.role === 'S' ? 'Supervisor' :
                         consultant.role === 'U' ? 'Superuser' :
                         consultant.role === 'C' ? 'User' : consultant.role || "N/A"}
                        <span style={{ position: "absolute", right: "0px", top: "50%", transform: "translateY(-50%)" }}>
                          <EditConsultantRoleModal
                            consultant={consultant}
                            update_state={this.update_state}
                          />
                        </span>
                      </div>

                      {/* Can Assign Tasks */}
                      <div className={"info_descr"} style={{ marginTop: 16 }}>
                        <MdTask style={overviewIconStyle} /> Can Assign Tasks
                      </div>
                      <div className={"info_span"} style={{ position: "relative" }}>
                        {consultant.canassigntask ? 
                          <MdCheckCircle style={{ color: 'green' }} /> : 
                          <MdCancel style={{ color: 'red' }} />}
                        <span style={{ position: "absolute", right: "0px", top: "50%", transform: "translateY(-50%)" }}>
                          <EditConsultantCanAssignTaskModal
                            consultant={consultant}
                            update_state={this.update_state}
                          />
                        </span>
                      </div>

                      {/* Active */}
                      <div className={"info_descr"} style={{ marginTop: 16 }}>
                        <MdCheckCircleOutline style={overviewIconStyle} /> Active
                      </div>
                      <div className={"info_span"} style={{ position: "relative" }}>
                        {consultant.active ? 
                          <MdCheckCircle style={{ color: 'green' }} /> : 
                          <MdCancel style={{ color: 'red' }} />}
                        <span style={{ position: "absolute", right: "0px", top: "50%", transform: "translateY(-50%)" }}>
                          <EditConsultantActiveModal
                            consultant={consultant}
                            update_state={this.update_state}
                          />
                        </span>
                      </div>

                      {/* Cash Passport Countries */}
                      <div className={"info_descr"} style={{ marginTop: 16 }}>
                        <GiMoneyStack style={overviewIconStyle} /> Accounting countries
                      </div>
                      <div className={"info_span"} style={{ position: "relative" }}>
                        {this.formatCashPassportDisplay(consultant.cashpassport)}
                        <span style={{ position: "absolute", right: "0px", top: "50%", transform: "translateY(-50%)" }}>
                          <EditConsultantCashPassportModal
                            consultant={consultant}
                            update_state={this.update_state}
                          />
                        </span>
                      </div>
                    </Card.Body>
                    <Card.Footer></Card.Footer>
                  </Card>
                </Grid.Column>
              </Grid>

              <Grid stackable columns={2}>
                <Grid.Column>
                  <Card style={{ marginTop: 20 }}>
                    <Card.Header>
                      <BsInfoSquare style={{ color: "#93ab3c", fontSize: "1.5em", marginRight: "0.5em" }} />
                      Projects
                    </Card.Header>
                    <Card.Body>
                      {Array.isArray(this.state.projects) && this.state.projects.length > 0 ? (
                        <ul className="list-unstyled" style={{ margin: 0 }}>
                          {this.state.projects.map((p, idx) => (
                            <li key={p.project_id} style={{ padding: '10px 0', borderBottom: '1px solid #eee' }}>
                              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px' }}>
                                <span style={labelPillStyle}>#</span>
                                <span style={valueTextStyle}>{idx + 1}</span>
                                <span style={{ width: 10 }} />
                                <span style={labelPillStyle}>ID</span>
                                <a href={`/data_management/project/${p.project_id}`} className="pillLink" style={{ ...valueTextStyle }}>{p.project_id}</a>
                                <span style={{ width: 10 }} />
                                <span style={labelPillStyle}>Title</span>
                                <span style={valueTextStyle}>{p.title}</span>
                                <span style={{ width: 10 }} />
                                <span style={labelPillStyle}>Status</span>
                                <span style={valueTextStyle}>{p.status || 'N/A'}</span>
                              </div>
                              {/* Inline tasks under project */}
                              {(() => {
                                const tp = this.state.tasksByProject?.[p.project_id] || { all: [] };
                                const allTasks = Array.isArray(tp.all) ? tp.all : [];
                                if (!allTasks.length) return null;
                                return (
                                  <div style={{ marginTop: 10, paddingLeft: 12 }}>
                                    <div>
                                      <div style={{ marginBottom: 6, fontWeight: 700 }}>Tasks</div>
                                      <ul className="list-unstyled" style={{ margin: 0 }}>
                                        {allTasks.map((t, tidx) => (
                                          <li key={t.projtask_id || tidx} style={{ padding: '6px 0' }}>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px' }}>
                                              <span style={labelPillStyle}>#</span>
                                              <span style={valueTextStyle}>{tidx + 1}</span>
                                              <span style={{ width: 10 }} />
                                              <span style={labelPillStyle}>ID</span>
                                              <a href={`/data_management/project_task/${t.projtask_id}`} className="pillLink" style={{ ...valueTextStyle }}>{t.projtask_id}</a>
                                              <span style={{ width: 10 }} />
                                              <span style={labelPillStyle}>Title</span>
                                              <span style={valueTextStyle}>{t.title}</span>
                                              <span style={{ width: 10 }} />
                                              <span style={labelPillStyle}>Status</span>
                                              <span style={valueTextStyle}>{t.status || 'N/A'}</span>
                                            </div>
                                            {(() => {
                                              const comments = (this.state.taskCommentsByProject?.[p.project_id]?.[t.projtask_id]) || [];
                                              if (!comments.length) return null;
                                              return (
                                                <div style={{ marginTop: 6, paddingLeft: 12, borderLeft: '3px solid #eef5ff' }}>
                                                  <div style={{ marginBottom: 4, fontWeight: 700, color: '#2c3e50' }}>Comments</div>
                                                  <ul className="list-unstyled" style={{ margin: 0 }}>
                                                    {comments.map((cm, cidx) => (
                                                      <li key={cm.taskcomm_id || cidx} style={{ padding: '6px 0', borderBottom: '1px dashed #eee' }}>
                                                        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px' }}>
                                                          <span style={labelPillStyle}>#</span>
                                                          <span style={valueTextStyle}>{cidx + 1}</span>
                                                          {cm.commentregistration ? (<>
                                                            <span style={{ width: 10 }} />
                                                            <span style={labelPillStyle}>Time</span>
                                                            <span style={valueTextStyle}>{formatDisplayDateTime(cm.commentregistration)}</span>
                                                          </>) : null}
                                                          {cm.consultant ? (<>
                                                            <span style={{ width: 10 }} />
                                                            <span style={labelPillStyle}>By</span>
                                                            <span style={valueTextStyle}>{cm.consultant.fullname || `${cm.consultant.surname || ''} ${cm.consultant.name || ''}`.trim()}</span>
                                                          </>) : null}
                                                          {cm.comment ? (<>
                                                            <span style={{ width: 10 }} />
                                                            <span style={labelPillStyle}>Comment</span>
                                                            <RichTextDisplay html={cm.comment} emptyLabel="" />
                                                          </>) : null}
                                                        </div>
                                                      </li>
                                                    ))}
                                                  </ul>
                                                </div>
                                              );
                                            })()}
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  </div>
                                );
                              })()}
                            </li>
                          ))}
                        </ul>
                      ) : (<div>No projects</div>)}
                    </Card.Body>
                    <Card.Footer>
                      <AddProjectModal defaultConsultantId={this.state.consultant?.consultant_id} lockConsultant={true} onProjectCreated={() => this.componentDidMount()} />
                    </Card.Footer>
                  </Card>
                </Grid.Column>
                <Grid.Column>
                  <Card style={{ marginTop: 20 }}>
                    <Card.Header>
                      <BsInfoSquare style={{ color: "#93ab3c", fontSize: "1.5em", marginRight: "0.5em" }} />
                      Accounting
                    </Card.Header>
                    <AccountingCurrencyRateBar barStyle={{ marginTop: 12, marginBottom: 4 }} />
                    <Card.Body>
                      {Array.isArray(this.state.cashItems) && this.state.cashItems.length > 0 ? (
                        <ul className="list-unstyled" style={{ margin: 0 }}>
                          {this.state.cashItems.map((c, idx) => (
                            <li key={c.cash_id} style={{ padding: '10px 0', borderBottom: '1px solid #eee' }}>
                              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px', width: '100%' }}>
                                <span style={labelPillStyle}>#</span>
                                <span style={valueTextStyle}>{idx + 1}</span>
                                <span style={{ width: 10 }} />
                                <span style={labelPillStyle}>ID</span>
                                <a href={`/data_management/cash/${c.cash_id}`} className="pillLink" style={{ ...valueTextStyle }}>{c.cash_id}</a>
                                <span style={{ width: 10 }} />
                                <span style={labelPillStyle}>Date</span>
                                <span style={valueTextStyle}>{c.trandate ? formatDisplayDate(c.trandate) : ''}</span>
                                <span style={{ width: 10 }} />
                                <span style={labelPillStyle}>Kind</span>
                                <span style={valueTextStyle}>{c.kind === 'E' ? 'Expense' : 'Payment'}</span>
                                {c?.country?.currency ? (<>
                                  <span style={{ width: 10 }} />
                                  <span style={labelPillStyle}>Currency</span>
                                  <span style={valueTextStyle}>{c.country.currency}</span>
                                </>) : null}
                                <CashTransactionActions
                                  cash={c}
                                  refreshData={() => this.componentDidMount()}
                                  lockConsultant={true}
                                />
                              </div>
                            </li>
                          ))}
                        </ul>
                      ) : (<div>No accounting entries</div>)}
                      <AccountingTotalSummary transactions={this.state.cashItems} />
                    </Card.Body>
                    <Card.Footer>
                      <AddCashModal defaultConsultantId={this.state.consultant?.consultant_id} lockConsultant={true} refreshData={() => this.componentDidMount()} />
                    </Card.Footer>
                  </Card>
                </Grid.Column>
              </Grid>
            </>
          ) : (
            loader()
          )}
        </div>
        <Footer />
      </>
    );
  }
}

export default ConsultantOverview;
